import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber, timeAgo } from "@/lib/format";
import {
  CrosshairIcon, CircleNotchIcon, ArrowsClockwiseIcon, PlusIcon, TrashIcon,
  YoutubeLogoIcon, EyeIcon, ThumbsUpIcon, ChatCircleIcon, SparkleIcon, RocketLaunchIcon,
} from "@phosphor-icons/react";

export const CompetitorRadar = ({ channelId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [compQuery, setCompQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [adapted, setAdapted] = useState({});

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const res = await api.get(`/competitors/radar?channel_id=${channelId}${refresh ? "&refresh=true" : ""}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Competitor Radar");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [channelId]);

  useEffect(() => { load(false); }, [load]);

  const addCompetitor = async () => {
    if (!compQuery.trim()) return;
    setAdding(true);
    try {
      await api.post(`/competitors/channels?channel_id=${channelId}`, { query: compQuery.trim() });
      toast.success("Competitor added — refreshing radar");
      setCompQuery("");
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add competitor");
    } finally { setAdding(false); }
  };

  const removeCompetitor = async (ytId) => {
    try {
      await api.delete(`/competitors/channels/${ytId}?channel_id=${channelId}`);
      toast.success("Competitor removed");
      await load(true);
    } catch { toast.error("Could not remove"); }
  };

  const adapt = async (v) => {
    const ins = v.insight || {};
    try {
      await api.post(`/competitors/adapt?channel_id=${channelId}`, {
        title: ins.idea_title || v.title,
        concept: ins.idea_concept || "",
        hook: "",
      });
      setAdapted((s) => ({ ...s, [v.video_id]: true }));
      toast.success("Added to your FSP missions — generate it from the FSP tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not adapt idea");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="radar-loading">
        <CircleNotchIcon size={36} className="text-[#16C784] spin-slow" />
      </div>
    );
  }

  const items = data?.items || [];
  const competitors = data?.competitors || [];

  return (
    <div data-testid="competitor-radar" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-rajdhani font-bold text-2xl tracking-wide text-white flex items-center gap-2">
            <CrosshairIcon size={22} weight="fill" className="text-[#16C784]" /> Competitor Radar
          </h2>
          <p className="font-mono text-xs text-[#5a5a63] mt-1">
            Top {data?.content_type === "shorts" ? "Shorts" : "long-form videos"} from other channels in your niche
            {data?.keywords?.length ? ` · matching: ${data.keywords.join(" · ")}` : ""}
          </p>
        </div>
        <button
          data-testid="radar-refresh"
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-2 border border-[#16C784] text-[#16C784] hover:bg-[#16C784]/10 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors disabled:opacity-50 w-fit"
        >
          <ArrowsClockwiseIcon size={14} className={refreshing ? "spin-slow" : ""} />
          {refreshing ? "Scanning…" : "Refresh Radar"}
        </button>
      </div>

      {/* Manual competitors */}
      <div className="border border-[#222226] bg-[#0a0a0c] p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5a5a63] mb-2">Track specific competitors</div>
        <div className="flex gap-2">
          <input
            data-testid="radar-competitor-input"
            value={compQuery}
            onChange={(e) => setCompQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCompetitor()}
            placeholder="Channel URL, @handle, or name"
            className="flex-1 bg-[#050505] border border-[#222226] focus:border-[#16C784] text-white font-mono text-sm px-3 py-2 outline-none transition-colors"
          />
          <button
            data-testid="radar-add-competitor"
            onClick={addCompetitor}
            disabled={adding || !compQuery.trim()}
            className="flex items-center gap-1.5 bg-[#16C784] text-black hover:bg-[#13b476] font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors disabled:opacity-50"
          >
            {adding ? <CircleNotchIcon size={14} className="spin-slow" /> : <PlusIcon size={14} weight="bold" />} Add
          </button>
        </div>
        {competitors.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {competitors.map((c) => (
              <div key={c.yt_channel_id} data-testid={`radar-competitor-${c.yt_channel_id}`}
                className="flex items-center gap-2 border border-[#222226] bg-[#050505] pl-1 pr-2 py-1">
                {c.thumbnail && <img src={c.thumbnail} alt="" referrerPolicy="no-referrer" className="w-5 h-5 rounded-full" />}
                <span className="font-mono text-xs text-[#A0A0AB]">{c.title}</span>
                <button onClick={() => removeCompetitor(c.yt_channel_id)}
                  data-testid={`radar-remove-${c.yt_channel_id}`} className="text-[#5a5a63] hover:text-[#FF5C5C]">
                  <TrashIcon size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Results grid */}
      {items.length === 0 ? (
        <div className="text-center py-20 font-mono text-sm text-[#5a5a63]" data-testid="radar-empty">
          No similar posts found yet. Add a competitor above or hit Refresh Radar.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="radar-grid">
          {items.map((v, i) => (
            <motion.div
              key={v.video_id}
              data-testid={`radar-card-${v.video_id}`}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className="border border-[#222226] bg-[#0a0a0c] flex flex-col hover:border-[#16C784]/40 transition-colors"
            >
              <a href={v.url} target="_blank" rel="noreferrer" className="relative group">
                {v.thumbnail && (
                  <img src={v.thumbnail} alt={v.title} referrerPolicy="no-referrer"
                    className="w-full aspect-video object-cover" />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                  <YoutubeLogoIcon size={40} weight="fill" className="text-[#FF0000] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {v.source === "manual" && (
                  <span className="absolute top-2 left-2 bg-[#16C784] text-black font-mono text-[9px] uppercase px-1.5 py-0.5">Tracked</span>
                )}
              </a>
              <div className="p-3 flex flex-col flex-1">
                <div className="font-rajdhani font-bold text-sm text-white leading-snug line-clamp-2">{v.title}</div>
                <div className="font-mono text-[11px] text-[#5a5a63] mt-1">{v.channel_title} · {timeAgo(v.published_at)}</div>

                <div className="flex items-center gap-3 mt-2 font-mono text-[11px] text-[#A0A0AB]">
                  <span className="flex items-center gap-1"><EyeIcon size={12} className="text-[#16C784]" /> {formatNumber(v.view_count)}</span>
                  <span className="flex items-center gap-1"><ThumbsUpIcon size={12} className="text-[#A3E635]" /> {formatNumber(v.like_count)}</span>
                  <span className="flex items-center gap-1"><ChatCircleIcon size={12} className="text-[#5a5a63]" /> {formatNumber(v.comment_count)}</span>
                  <span className="ml-auto text-[#16C784]">{v.engagement_rate}%</span>
                </div>

                {v.insight && (
                  <div className="mt-3 border-t border-[#222226] pt-2 flex-1">
                    <p className="font-mono text-[11px] text-[#A0A0AB] leading-relaxed">
                      <span className="text-[#16C784]">Why it works:</span> {v.insight.why}
                    </p>
                    {v.insight.idea_title && (
                      <div className="mt-2 bg-[#050505] border border-[#222226] p-2">
                        <div className="font-mono text-[9px] uppercase tracking-wider text-[#5a5a63] flex items-center gap-1">
                          <SparkleIcon size={11} className="text-[#A3E635]" /> Idea for you
                        </div>
                        <div className="font-rajdhani font-bold text-xs text-white mt-0.5">{v.insight.idea_title}</div>
                        {v.insight.idea_concept && (
                          <div className="font-mono text-[10px] text-[#5a5a63] mt-0.5 leading-relaxed">{v.insight.idea_concept}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  data-testid={`radar-adapt-${v.video_id}`}
                  onClick={() => adapt(v)}
                  disabled={adapted[v.video_id]}
                  className="mt-3 flex items-center justify-center gap-1.5 border border-[#A3E635]/40 text-[#A3E635] hover:bg-[#A3E635]/10 font-rajdhani font-bold uppercase tracking-wider text-[11px] px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-default"
                >
                  <RocketLaunchIcon size={12} /> {adapted[v.video_id] ? "Added to FSP" : "Adapt to my channel"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {data?.generated_at && (
        <div className="font-mono text-[10px] text-[#5a5a63] text-center pt-2">
          Last scanned {timeAgo(data.generated_at)} · results cached ~24h to save API quota
        </div>
      )}
    </div>
  );
};

export default CompetitorRadar;
